import { lazy } from "react";

const RELOAD_KEY = "chunk_load_reload_at";
const RELOAD_GUARD_MS = 60 * 1000;

// Chrome, Safari, Firefox, and Vite's CSS preload helper word the same
// failure differently.
const CHUNK_LOAD_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
  /Unable to preload CSS/i,
];

export function isChunkLoadError(error) {
  const message = error?.message || "";
  return CHUNK_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Vite marks a CSS dependency as seen before it loads. On a second import() it
// skips that file, so the module would render unstyled. Load the file again
// here, and fail the retry if it still does not load.
function reloadFailedStylesheet(error) {
  const href = error?.message?.match(/Unable to preload CSS for (\S+)/)?.[1];
  if (!href) return Promise.resolve();

  const failedLink = document.querySelector(
    `link[rel="stylesheet"][href="${href}"]`
  );
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  if (failedLink?.nonce) link.setAttribute("nonce", failedLink.nonce);

  return new Promise((resolve, reject) => {
    link.addEventListener("load", () => {
      failedLink?.remove();
      resolve();
    });
    link.addEventListener("error", () => {
      link.remove();
      reject(error);
    });
    document.head.appendChild(link);
  });
}

// Reloads the page at most once per guard window, so a chunk that never loads
// cannot put the page into a reload loop.
function reloadOnce() {
  try {
    const lastReloadAt = Number(window.sessionStorage.getItem(RELOAD_KEY));
    if (lastReloadAt && Date.now() - lastReloadAt < RELOAD_GUARD_MS) {
      return false;
    }
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch (e) {
    return false;
  }
  window.location.reload();
  return true;
}

export async function importWithRetry(
  importFn,
  { retries = 2, delayMs = 1000 } = {}
) {
  let lastError;
  for (let attempt = 0; attempt <= retries; attempt++) {
    if (attempt > 0) await wait(delayMs * attempt);
    try {
      if (lastError) await reloadFailedStylesheet(lastError);
      return await importFn();
    } catch (error) {
      if (!isChunkLoadError(error)) throw error;
      lastError = error;
    }
  }
  // Keep the Suspense fallback on screen while the page reloads.
  if (reloadOnce()) return new Promise(() => {});
  throw lastError;
}

export default function lazyWithRetry(importFn) {
  return lazy(() => importWithRetry(importFn));
}
